import * as React from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import theme, { Theme } from 'helpers/theme';
import { createFragmentContainer, graphql } from 'react-relay';
import { Router, useRouter } from 'found';
import PageTitle from 'components/PageTitle';
import Table, { RowCell } from 'components/Table';
import useTableSearch from 'components/Table/useTableSearch';
import { useActionMenuDispatch } from 'components/ActionMenu/ActionMenuProvider';
import { SecurityProgrammes_manager } from './__generated__/SecurityProgrammes_manager.graphql';
import moment from 'moment';
import Button from 'components/Button/Button';

type SecurityProgramme = {
  uuid: string;
  name: string;
  createdAt: string;
  downloadURL: string;
};

const useStyles = createUseStyles((theme: Theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    padding: '40px 40px'
  },
  title: {
    fontSize: 14,
    fontWeight: 700,
    textWrap: 'nowrap',
    cursor: 'pointer'
  },
  rowText: {
    fontSize: 13,
    textWrap: 'nowrap',
    fontWeight: 400
  },
  dateCell: {},
  dateFrom: {
    color: '#6A728F',
    fontSize: 11
  },
  spacer: {
    marginBottom: 60
  }
}));

export const DEFAULT_LIMIT = 10;

type SecurityProgrammePage = {
  edges: SecurityProgramme[];
  pageInfo: {
    total: number;
    offset: number;
    limit: number;
    given: number;
  };
};

type Props = {
  manager: { company: { securityProgrammes: SecurityProgrammePage } };
};

const SecurityProgrammes = ({ manager }: Props) => {
  const securityProgrammes = manager.company.securityProgrammes;
  const theme = useTheme();
  const classes = useStyles({ theme });

  const { router, match } = useRouter();
  const [cachedProgrammes, setCachedProgrammes] = React.useState(
    securityProgrammes
  );

  React.useEffect(() => {
    if (securityProgrammes != null) {
      setCachedProgrammes(securityProgrammes);
    }
  }, [securityProgrammes]);

  const formatRow: RowCell<SecurityProgramme>[] = [
    {
      header: 'Programme Name',
      width: '100%',
      format: (programme) => {
        return (
          <div
            className={classes.title}
            onClick={() => {
              window.open(programme.downloadURL, '_blank');
            }}
          >
            {programme.name}
          </div>
        );
      }
    },
    {
      header: 'Last Updated',
      minWidth: 146,
      format: (programme) => {
        return (
          <div className={classes.dateCell}>
            <div className={classes.rowText}>
              {moment(programme.createdAt).format('DD/MM/YYYY')}
            </div>
            <div className={classes.dateFrom}>
              {moment(programme.createdAt).fromNow()}
            </div>
          </div>
        );
      }
    },
    {
      header: 'Download',
      minWidth: 120,
      format: (programme) => {
        return (
          <Button
            archetype="flat"
            onClick={() => {
              window.open(programme.downloadURL, '_blank');
            }}
          >
            Download
          </Button>
        );
      }
    }
  ];

  const pageUrl = (
    page?: { offset: number; limit: number },
    searchQuery?: string | null
  ) => {
    if (!searchQuery) {
      if (searchQuery === '') {
        searchQuery = '';
      } else {
        searchQuery = match.location.query.search ?? '';
      }
    }

    if (!page) {
      if (match.location.query.offset) {
        page = {
          offset: parseInt(match.location.query.offset),
          limit: parseInt(match.location.query.limit)
        };
      }
    }

    let url = `/app/security-programmes?`;

    if (page) {
      url += `offset=${page.offset}&limit=${page.limit}&`;
    }

    if (searchQuery) {
      url += `search=${searchQuery}&`;
    }

    return url;
  };

  const onNextPage = () => {
    let { offset, limit, total } = securityProgrammes.pageInfo;

    if (offset + limit >= total) {
      return;
    }

    router.push(pageUrl({ offset: Math.min(offset + limit, total), limit }));
  };

  const onSearch = useTableSearch((query: string) =>
    pageUrl({ offset: 0, limit: DEFAULT_LIMIT }, query)
  );

  const onPreviousPage = () => {
    let { offset, limit } = securityProgrammes.pageInfo;
    router.push(pageUrl({ offset: Math.max(offset - limit, 0), limit }));
  };

  return (
    <div className={classes.root}>
      <PageTitle
        title="Security Programmes"
        subTitle="View and download your company's security programme documents."
      />
      <div className={classes.spacer} />
      <Table<SecurityProgramme>
        tableName="security programmes"
        data={cachedProgrammes?.edges ?? []}
        formatRow={formatRow}
        onNextPage={onNextPage}
        onPreviousPage={onPreviousPage}
        pageInfo={cachedProgrammes?.pageInfo}
        emptyTableText="No security programmes have been uploaded for your company yet."
        onSearch={onSearch}
        searchValue={match.location.query.search}
      />
    </div>
  );
};

const SecurityProgrammesFrag = createFragmentContainer(SecurityProgrammes, {
  manager: graphql`
    fragment SecurityProgrammes_manager on Manager {
      company {
        securityProgrammes(page: $page, filter: $filter) {
          edges {
            uuid
            name
            createdAt
            downloadURL
          }
          pageInfo {
            total
            offset
            limit
            given
          }
        }
      }
    }
  `
});

export default SecurityProgrammesFrag;
